/**
 * FuelEvolutionInsight — 09/02/2026 (Q2 Lot I user prod).
 *
 * Placé sous le graphique d'évolution de la trésorerie. Affiche :
 *   • L'évolution récente du cours carburant (état actuel + delta vs base)
 *   • Si Rémi Forni est recruté (role-christophe-faure), une anticipation
 *     du cours sur les 7 prochains jours (basée sur la date de fin du
 *     choc actif et son sens — hausse/baisse).
 *
 * User request : « L'indication carburant et on pourrait ajouter son
 * évolution éventuellement une anticipation estimée par Rémi forni si
 * recruté devrait apparaitre en dessous du graphique évolution de la
 * trésorerie. »
 */
import React from 'react';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { TrendingUp, TrendingDown, Minus, Fuel, Eye, Sparkles } from 'lucide-react';
import { computeFuelOptimizationBreakdown } from '../../store/helpers/computeFuelOptimizationBreakdown';

function buildForecast(fpm, totalDays) {
  // Si pas de choc → stable
  if (!fpm || !Number.isFinite(fpm.expiresOnDay) || totalDays >= fpm.expiresOnDay) {
    return { trend: 'stable', label: 'Stable', detail: 'Pas de choc anticipé sur les 7 prochains jours.' };
  }
  const daysLeft = fpm.expiresOnDay - totalDays;
  const multiplier = fpm.multiplier || 1.0;
  const direction = multiplier > 1 ? 'hausse' : 'baisse';
  const deltaPct = Math.round(Math.abs(multiplier - 1) * 100);
  // Anticipation : sur les 7 prochains jours, le choc va se résorber
  // progressivement à l'expiration. On indique le sens + l'horizon.
  if (daysLeft <= 7) {
    return {
      trend: multiplier > 1 ? 'down' : 'up',
      label: `Retour à la base J+${daysLeft}`,
      detail: `Le choc en ${direction} (-${deltaPct} %) expire dans ${daysLeft} j. Anticiper l'amortissement.`,
    };
  }
  return {
    trend: multiplier > 1 ? 'up' : 'down',
    label: `${direction} ${deltaPct}% jusqu'à J+${daysLeft}`,
    detail: `Choc en ${direction} (-${deltaPct} %) actif encore ${daysLeft} j. Au-delà : retour base.`,
  };
}

export default function FuelEvolutionInsight() {
  const fpm = useGameStore(s => s.fuelPriceModifier);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const hiredSupport = useGameStore(s => s.hiredSupport || EMPTY_OBJECT);
  const activeInvestments = useGameStore(s => s.activeInvestments) || EMPTY_OBJECT;
  const activeResolution = useGameStore(s => s.activeResolution);
  const hasRemi = !!hiredSupport['role-christophe-faure'];
  const gameStarted = useGameStore(s => s.gameStarted);

  if (!gameStarted) return null;

  const active = !!(fpm && Number.isFinite(fpm.expiresOnDay) && totalDays < fpm.expiresOnDay);
  const multiplier = active ? (fpm.multiplier || 1.0) : 1.0;
  const marketIndex = Math.round(100 * multiplier);

  // 10/02/2026 (Lot N P1) — Retour user prod : « Karim Benali, censé faire
  // baisser le carburant de -5%, je reste sur une base 100 sur le widget ».
  // On affiche désormais : cours marché + optimisations cumulées + index
  // effectif vraiment payé par le joueur.
  const optimizations = computeFuelOptimizationBreakdown({
    activeInvestments, hiredSupport, activeResolution, totalDays,
  });
  const effectiveMultiplier = multiplier * (1 - optimizations.total);
  const effectiveIndex = Math.round(100 * effectiveMultiplier);
  const isUp = effectiveMultiplier > 1;
  const isDown = effectiveMultiplier < 1;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const tone = isUp ? 'rose' : isDown ? 'emerald' : 'slate';

  const forecast = hasRemi ? buildForecast(fpm, totalDays) : null;

  return (
    <div
      className="rounded-lg border border-ink-600 bg-ink-700/50 p-3 mt-2"
      data-testid="fuel-evolution-insight"
    >
      <div className="flex items-center gap-2 mb-2">
        <Fuel className="w-4 h-4 text-amber-400" />
        <h4 className="text-xs font-display font-bold text-foreground uppercase tracking-wide">
          Cours carburant
        </h4>
        <span className="ml-auto text-[10px] text-slate-500 font-mono">Base 100</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {/* Maintenant */}
        <div className="bg-ink-800/50 rounded-md px-2 py-1.5">
          <p className="text-[10px] uppercase text-slate-500 font-mono mb-0.5">Aujourd&apos;hui</p>
          <div className="flex items-baseline gap-1">
            <span
              className={`text-xl font-mono font-black ${
                tone === 'rose' ? 'text-rose-200' : tone === 'emerald' ? 'text-emerald-200' : 'text-slate-200'
              }`}
              data-testid="fuel-insight-index"
              title={
                optimizations.total > 0
                  ? `Cours marché ${marketIndex} − vos optimisations ${Math.round(optimizations.total * 100)}% = ${effectiveIndex}`
                  : `Cours marché ${marketIndex}`
              }
            >
              {effectiveIndex}
            </span>
            <Icon
              className={`w-4 h-4 ${
                tone === 'rose' ? 'text-rose-400' : tone === 'emerald' ? 'text-emerald-400' : 'text-slate-400'
              }`}
            />
          </div>
          {(active || optimizations.total > 0) && (
            <p className="text-[10px] text-slate-400 mt-0.5">
              {isUp ? '+' : ''}{Math.round((effectiveMultiplier - 1) * 100)} % vs base
            </p>
          )}
          {optimizations.total > 0 && (
            <div
              className="mt-1 pt-1 border-t border-ink-600/60 flex items-center gap-1 text-[10px] text-emerald-300"
              data-testid="fuel-insight-optimizations"
              title={optimizations.sources.map(s => `• ${s.label} (-${Math.round(s.pct * 100)}%)`).join('\n')}
            >
              <Sparkles className="w-2.5 h-2.5" />
              <span className="font-mono font-bold">
                -{Math.round(optimizations.total * 100)}%
              </span>
              <span className="text-emerald-400/70">{optimizations.sources.length} optim.</span>
            </div>
          )}
        </div>

        {/* Anticipation Rémi */}
        {hasRemi ? (
          <div
            className="bg-sky-500/10 border border-sky-400/40 rounded-md px-2 py-1.5"
            data-testid="fuel-insight-forecast"
            title="Anticipation fournie par Rémi Forni (Responsable exploitation)"
          >
            <p className="text-[10px] uppercase text-sky-300 font-mono mb-0.5 flex items-center gap-1">
              <Eye className="w-2.5 h-2.5" />
              Rémi anticipe J+7
            </p>
            <p className="text-xs font-bold text-sky-100 leading-tight">
              {forecast.label}
            </p>
            <p className="text-[10px] text-sky-200/70 mt-0.5 line-clamp-2">
              {forecast.detail}
            </p>
          </div>
        ) : (
          <div
            className="bg-slate-700/30 border border-slate-600 rounded-md px-2 py-1.5 opacity-70"
            data-testid="fuel-insight-no-remi"
          >
            <p className="text-[10px] uppercase text-slate-500 font-mono mb-0.5">Anticipation J+7</p>
            <p className="text-[10px] text-slate-400 leading-tight">
              Recrutez <strong className="text-slate-300">Rémi Forni</strong> (Resp. exploitation) pour débloquer les prévisions.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
